import React, { useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import RetroLoadingDots from '../RetroLoadingDots';
import TokenBalance from '../TokenBalance';
import { assetUrl } from '../../utils/photoslopApi';

const STYLING_OPTIONS = [
  { key: '', label: 'None' },
  { key: 'eggshell-bird', label: 'Eggshell' },
  { key: 'liquid-glass', label: 'Glass' },
  { key: 'social-post', label: 'Social' },
  { key: 'clean-bird', label: 'Clean' },
  { key: 'event-graphic', label: 'Event' },
];

const ASPECTS = ['1:1', '16:9', '9:16', '4:5'];
const COUNTS = [1, 2, 4];

function RefChip({ asset, n, isRoot, onRemove, onInsert, onOpenSpices }) {
  const src = assetUrl(asset.path);
  return (
    <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-lg border-2 border-[#00FF9F]/50 bg-white">
      <img src={src} alt="" className="h-full w-full object-cover" />
      <span className="absolute left-0 top-0 rounded-br bg-black/75 px-1 font-['VT323'] text-[10px] text-[#00FF9F]">
        @{n}
      </span>
      <div className="absolute inset-0 flex items-center justify-center gap-0.5 bg-black/50 opacity-0 transition hover:opacity-100">
        <button
          type="button"
          onClick={onInsert}
          className="rounded bg-white px-1 font-['VT323'] text-[10px] text-black"
        >
          @
        </button>
        {isRoot ? (
          <button
            type="button"
            onClick={onOpenSpices}
            className="rounded bg-white px-1 font-['VT323'] text-[10px] text-black"
          >
            +
          </button>
        ) : (
          <button
            type="button"
            onClick={onRemove}
            className="rounded bg-white px-1 font-['VT323'] text-[10px] text-black"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}

export default function PhotoslopCompose({
  root,
  secondaries,
  promptText,
  styling,
  count,
  aspect,
  generating,
  generateError,
  availableTokens,
  isFounder,
  monthlyUsage,
  onPromptChange,
  onRebuildPrompt,
  onStylingChange,
  onCount,
  onAspect,
  onSend,
  onRemoveSecondary,
  onClearRoot,
  onInsertToken,
  onOpenSpices,
}) {
  const taRef = useRef(null);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const totalSlots = (root ? 1 : 0) + secondaries.length;
  const bypassTokens =
    availableTokens === Infinity || availableTokens > 9999;
  const disabled = generating || !root || !promptText.trim();
  const insufficientTokens = !bypassTokens && availableTokens < count;

  const coloredPrompt = useMemo(() => {
    const re = /(@\d+)\b/g;
    const parts = [];
    let last = 0;
    let m;
    while ((m = re.exec(promptText)) !== null) {
      if (m.index > last) parts.push(promptText.slice(last, m.index));
      const n = parseInt(m[1].slice(1), 10);
      const ok = n >= 1 && n <= totalSlots;
      parts.push(
        <span key={m.index} className={ok ? 'text-[#00FF9F]' : 'text-[#FF1493]'}>
          {m[1]}
        </span>,
      );
      last = m.index + m[0].length;
    }
    if (last < promptText.length) parts.push(promptText.slice(last));
    return parts.length ? parts : promptText;
  }, [promptText, totalSlots]);

  const optionsSummary = [
    styling ? STYLING_OPTIONS.find((s) => s.key === styling)?.label : 'None',
    aspect,
    `×${count}`,
  ].join(' · ');

  return (
    <div className="flex-shrink-0 border-t border-black/8 bg-white shadow-[0_-4px_24px_rgba(0,0,0,0.06)]">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-2.5 px-3 py-3 sm:px-5">
        {/* Refs on their own row — scroll horizontally, never steal prompt width */}
        {root ? (
          <div className="-mx-1 flex items-center gap-1.5 overflow-x-auto px-1 pb-0.5 scrollbar-thin">
            <RefChip
              asset={root}
              n={1}
              isRoot
              onInsert={() => onInsertToken(1)}
              onRemove={onClearRoot}
              onOpenSpices={onOpenSpices}
            />
            {secondaries.map((s, i) => (
              <RefChip
                key={s.path}
                asset={s}
                n={i + 2}
                onInsert={() => onInsertToken(i + 2)}
                onRemove={() => onRemoveSecondary(s.path)}
                onOpenSpices={onOpenSpices}
              />
            ))}
            <button
              type="button"
              onClick={onOpenSpices}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border-2 border-dashed border-black/15 font-['VT323'] text-[18px] text-black/35 hover:border-[#00FF9F]/40 hover:text-black/55"
              title="Add reference images"
            >
              +
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onOpenSpices}
            className="w-full rounded-lg border-2 border-dashed border-[#00FF9F]/40 bg-[#00FF9F]/10 px-3 py-2 font-['VT323'] text-[16px] text-black/70 hover:bg-[#00FF9F]/20"
          >
            Pick @1 image →
          </button>
        )}

        {/* Prompt — full width */}
        <div className="relative w-full">
          <div
            aria-hidden
            className="pointer-events-none min-h-[96px] max-h-[200px] overflow-auto whitespace-pre-wrap break-words rounded-xl border border-black/8 bg-[#F4F3F0] px-4 py-3 font-['VT323'] text-[17px] leading-relaxed text-black/70"
          >
            {promptText ? (
              coloredPrompt
            ) : (
              <span className="text-black/25">
                Describe the scene — e.g. @1 and @2 at a summer game, golden hour…
              </span>
            )}
          </div>
          <textarea
            ref={taRef}
            value={promptText}
            onChange={(e) => onPromptChange(e.target.value)}
            disabled={generating || !root}
            rows={4}
            spellCheck={false}
            className="absolute inset-0 resize-none rounded-xl bg-transparent px-4 py-3 font-['VT323'] text-[17px] leading-relaxed text-transparent caret-black/60 focus:outline-none focus:ring-2 focus:ring-[#00FF9F]/25 disabled:cursor-not-allowed"
          />
          <AnimatePresence>
            {generating ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/80"
              >
                <RetroLoadingDots text="Generating" size="16px" className="text-white" />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setOptionsOpen((o) => !o)}
            className="font-['VT323'] text-[13px] text-black/40 hover:text-black/60"
          >
            {optionsOpen ? '▾' : '▸'} Options · {optionsSummary}
          </button>
          <div className="flex items-center gap-3">
            <TokenBalance balance={availableTokens} isFounder={isFounder} monthlyUsage={monthlyUsage} />
            <button
              type="button"
              disabled={disabled || insufficientTokens}
              onClick={onSend}
              className={`btn btn-green shrink-0 rounded-xl px-6 py-2.5 font-['VT323'] text-[20px] ${
                disabled || insufficientTokens
                  ? 'cursor-not-allowed opacity-40'
                  : ''
              }`}
            >
              Create
            </button>
          </div>
        </div>

        <AnimatePresence>
          {optionsOpen ? (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="flex flex-wrap items-center gap-2 border-t border-black/5 pt-2">
                <span className="font-['VT323'] text-[11px] text-black/30">Look</span>
                {STYLING_OPTIONS.map((s) => (
                  <button
                    key={s.key || 'none'}
                    type="button"
                    onClick={() => onStylingChange(s.key)}
                    className={`rounded-md px-2 py-0.5 font-['VT323'] text-[12px] ${
                      styling === s.key
                        ? 'bg-black text-[#00FF9F]'
                        : 'bg-[#F4F3F0] text-black/45 ring-1 ring-black/8'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
                <span className="ml-2 font-['VT323'] text-[11px] text-black/30">Ratio</span>
                {ASPECTS.map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => onAspect(a)}
                    className={`rounded-md px-2 py-0.5 font-['VT323'] text-[12px] ${
                      aspect === a
                        ? 'bg-[#0066FF]/12 text-[#0066FF] ring-1 ring-[#0066FF]/25'
                        : 'text-black/40'
                    }`}
                  >
                    {a}
                  </button>
                ))}
                <span className="font-['VT323'] text-[11px] text-black/30">Count</span>
                {COUNTS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => onCount(c)}
                    className={`rounded-md px-2 py-0.5 font-['VT323'] text-[12px] ${
                      count === c
                        ? 'bg-[#FF6B00]/12 text-[#FF6B00] ring-1 ring-[#FF6B00]/25'
                        : 'text-black/40'
                    }`}
                  >
                    ×{c}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={onRebuildPrompt}
                  className="ml-auto font-['VT323'] text-[12px] text-black/35 hover:text-black/55"
                >
                  Rebuild @tokens
                </button>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        {generateError ? (
          <p className="font-['VT323'] text-[14px] text-[#FF1493]">{generateError}</p>
        ) : null}
      </div>
    </div>
  );
}
